import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import * as LucideIcons from 'lucide-react';
import './FileUpload.css';
import { useAuth0 } from '@auth0/auth0-react';
import { showToast } from '../../common/ToastHandler';
import { useDataReload } from '../DataReloadContext';

interface UploadConfig {
  upload_init_action: {
    url: string;
    method: string;
    headers?: Record<string, string>;
  };
  accepted_file_types: string[];
  max_file_size_mb: number;
  multiple_files: boolean;
  height?: string;
  width?: string;
  icon?: {
    value: string;
    size: number;
    color: string;
  };
  upload_text: string;
  upload_subtext: string;
  success_message?: string;
  error_message?: string;
  show_file_list?: boolean;
}

interface UploadedFile {
  name: string;
  size: number;
  type: string;
  url: string;
}

interface UploadProgress {
  current: number;
  total: number;
  percentage: number;
  currentFile?: string | null;
  currentFileProgress?: number;
}

interface FileUploadProps {
  config: UploadConfig;
  onUploadComplete?: (files: UploadedFile[]) => void;
}

const FileUpload: React.FC<FileUploadProps> = ({
  config,
  onUploadComplete,
}) => {
  const { getIdTokenClaims } = useAuth0();
  const { reloadData } = useDataReload();
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [uploadStatus, setUploadStatus] = useState<string>('idle');
  const [uploadProgress, setUploadProgress] = useState<UploadProgress>({
    current: 0,
    total: 0,
    percentage: 0,
  });

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      setUploadStatus('uploading');
      setUploadProgress({
        current: 0,
        total: acceptedFiles.length,
        percentage: 0,
      });

      // Add a small delay to ensure the initial progress state is rendered
      await new Promise(resolve => setTimeout(resolve, 100));

      try {
        const token = await getIdTokenClaims();
        const headers = {
          ...config.upload_init_action.headers,
          Authorization: `Bearer ${token.__raw}`,
          'Content-Type': 'application/json',
        };

        // Create the items array with file information
        const items = acceptedFiles.map(file => ({
          filename: file.name,
          content_type: file.type,
        }));

        // First get the upload URLs from the init endpoint
        const initResponse = await fetch(config.upload_init_action.url, {
          method: config.upload_init_action.method,
          headers: headers,
          body: JSON.stringify({ items }),
        });

        if (!initResponse.ok) {
          throw new Error('Failed to initialize upload');
        }

        const { items: uploadItems } = await initResponse.json();

        // Upload each file using its specific URL with progress tracking
        const uploadedFilesList = [];

        for (let index = 0; index < acceptedFiles.length; index++) {
          const file = acceptedFiles[index];
          const uploadInfo = uploadItems[index];

          if (!uploadInfo || !uploadInfo.url) {
            throw new Error(`No upload URL provided for ${file.name}`);
          }

          try {
            console.log(
              `Starting upload for file ${index + 1}/${acceptedFiles.length}: ${file.name}`
            );

            // Use XMLHttpRequest for progress tracking
            await new Promise((resolve, reject) => {
              const xhr = new XMLHttpRequest();

              // Track upload progress
              xhr.upload.addEventListener('progress', event => {
                if (event.lengthComputable) {
                  const fileProgress = Math.round(
                    (event.loaded / event.total) * 100
                  );
                  const overallProgress = Math.round(
                    ((index + event.loaded / event.total) /
                      acceptedFiles.length) *
                      100
                  );

                  setUploadProgress({
                    current: index,
                    total: acceptedFiles.length,
                    percentage: overallProgress,
                    currentFile: file.name,
                    currentFileProgress: fileProgress,
                  });
                }
              });

              xhr.addEventListener('load', () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                  resolve(undefined);
                } else {
                  reject(
                    new Error(`Failed to upload ${file.name}: ${xhr.status}`)
                  );
                }
              });

              xhr.addEventListener('error', () => {
                reject(new Error(`Network error uploading ${file.name}`));
              });

              xhr.open('PUT', uploadInfo.url);
              xhr.setRequestHeader('Content-Type', file.type);
              xhr.send(file);
            });

            uploadedFilesList.push({
              name: file.name,
              size: file.size,
              type: file.type,
              url: uploadInfo.url,
            });

            // Update progress for completed file
            const completedCount = index + 1;
            const percentage = Math.round(
              (completedCount / acceptedFiles.length) * 100
            );

            setUploadProgress({
              current: completedCount,
              total: acceptedFiles.length,
              percentage: percentage,
              currentFile: null,
              currentFileProgress: 0,
            });

            console.log(
              `Upload completed: ${completedCount}/${acceptedFiles.length} (${percentage}%)`
            );
          } catch (error) {
            console.error(`Error uploading ${file.name}:`, error);
            throw error; // Re-throw to be caught by outer try-catch
          }
        }

        setUploadedFiles(prev => [...prev, ...uploadedFilesList]);
        setUploadStatus('success');
        setUploadProgress({ current: 0, total: 0, percentage: 0 });
        showToast(
          'success',
          config.success_message || 'Files uploaded successfully!'
        );

        // Refresh the uploaded files table
        reloadData('uploaded_files_table');

        if (onUploadComplete) {
          onUploadComplete(uploadedFilesList);
        }
      } catch (error) {
        console.error('Upload error:', error);
        setUploadStatus('error');
        setUploadProgress({ current: 0, total: 0, percentage: 0 });
        showToast(
          'error',
          config.error_message || 'Upload failed. Please try again.'
        );
      }
    },
    [config, onUploadComplete, reloadData]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: config.accepted_file_types.reduce((acc, type) => {
      acc[type] = [];
      return acc;
    }, {}),
    maxSize: config.max_file_size_mb * 1024 * 1024,
    multiple: config.multiple_files,
  });

  const LucideIcon: React.FC<{
    name: string;
    size?: number;
    color?: string;
  }> = ({ name, size = 24, color = '#666' }) => {
    const IconComponent = LucideIcons[name];
    if (!IconComponent) return null;
    return <IconComponent size={size} color={color} />;
  };

  return (
    <div
      className="file-upload-container"
      style={{ height: config.height, width: config.width }}
    >
      <div
        {...getRootProps()}
        className={`dropzone ${isDragActive ? 'active' : ''} ${uploadStatus === 'uploading' ? 'uploading' : ''}`}
      >
        <input {...getInputProps()} />
        <div className="upload-content">
          {config.icon && (
            <div className="upload-icon">
              <LucideIcon
                name={config.icon.value}
                size={config.icon.size}
                color={config.icon.color}
              />
            </div>
          )}
          <div className="upload-text">
            {uploadStatus === 'uploading' ? (
              <>
                <p className="primary-text">
                  {uploadProgress.currentFile
                    ? `Uploading: ${uploadProgress.currentFile}`
                    : 'Uploading files...'}
                </p>
                <p className="secondary-text">
                  {uploadProgress.currentFile &&
                  uploadProgress.currentFileProgress > 0 ? (
                    <>
                      File {uploadProgress.current + 1} of{' '}
                      {uploadProgress.total} (
                      {uploadProgress.currentFileProgress}%)
                    </>
                  ) : (
                    <>
                      {uploadProgress.current} of {uploadProgress.total} files
                      completed ({uploadProgress.percentage}%)
                    </>
                  )}
                </p>
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{
                      width: `${uploadProgress.percentage}%`,
                      backgroundColor: '#48bb78',
                      transition: 'width 0.2s ease',
                    }}
                  ></div>
                </div>
              </>
            ) : (
              <>
                <p className="primary-text">{config.upload_text}</p>
                <p className="secondary-text">{config.upload_subtext}</p>
              </>
            )}
          </div>
        </div>
      </div>

      {config.show_file_list && uploadedFiles.length > 0 && (
        <div className="uploaded-files">
          <h4>Uploaded Files:</h4>
          <ul>
            {uploadedFiles.map((file, index) => (
              <li key={index}>
                {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default FileUpload;
